import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Settings, Plus, X, RefreshCw, Sparkles, ExternalLink, Lock, Bell, Flame } from "lucide-react";
import { useBillingEnabled } from "./ProUpsellCard";
import { usePushNotifications } from "../hooks/usePushNotifications";

const BillingSection = ({ dm }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const billingEnabled = useBillingEnabled();

  useEffect(() => {
    axios.get(`${API}/billing/status`).then(r => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/billing/portal`);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the billing portal.");
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-xl p-5 ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`} data-testid="billing-section">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Sparkles className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
        <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
          {status?.is_pro ? "TourismIntel PRO — active" : "Free plan"}
        </p>
        {status?.beta_tester && (
          <span data-testid="beta-tester-badge"
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${dm ? "bg-purple-400/15 text-purple-300 ring-1 ring-purple-400/30" : "bg-purple-100 text-purple-700 ring-1 ring-purple-200"}`}>
            <Flame className="w-3 h-3" /> Beta Tester
          </span>
        )}
      </div>
      {status?.beta_tester && (
        <p data-testid="beta-tester-note" className={`text-xs mb-3 ${dm ? "text-purple-300/70" : "text-purple-700/70"}`}>
          You&apos;re part of the TourismIntel Beta club — early access, and a direct line to shape what we build next.
        </p>
      )}
      {status?.is_pro && (
        <p className={`text-xs mb-4 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
          Source: {status.pro_source || "—"}{status.pro_until ? ` · until ${new Date(status.pro_until).toLocaleDateString()}` : ""}
        </p>
      )}
      {status?.has_billing_profile ? (
        <button data-testid="billing-portal-btn" onClick={openPortal} disabled={busy}
          className={`px-5 py-2 rounded-full category-tag disabled:opacity-50 ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
          {busy ? "Opening…" : "Manage subscription"}
        </button>
      ) : billingEnabled === true ? (
        <a href="/pro" data-testid="billing-upgrade-link"
          className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-full category-tag ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
          {status?.is_pro ? "See PRO plans" : "Upgrade to PRO"}<ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        <p data-testid="billing-unavailable-note" className={`text-xs ${dm ? "text-slate-500" : "text-[#9aa7a9]"}`}>
          Plan upgrades will be available soon.
        </p>
      )}
    </div>
  );
};

const ProTeaser = ({ dm }) => (
  <div data-testid="feed-prefs-pro-teaser" className={`rounded-xl p-8 text-center ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`}>
    <div className={`w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center ${dm ? "bg-cyan-400/10" : "bg-[#006874]/8"}`}>
      <Lock className={`w-5 h-5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
    </div>
    <p className={`text-lg font-semibold mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
      Personalize your feed — a PRO feature
    </p>
    <p className={`text-sm mb-6 max-w-sm mx-auto ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
      Choose the topics you care about and hide the ones you don&apos;t. Your feed, your rules — updated on every visit.
    </p>
    <a href="/pro" data-testid="feed-prefs-upgrade-link"
      className={`inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full category-tag ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
      <Sparkles className="w-3.5 h-3.5" />Upgrade to PRO<ExternalLink className="w-3 h-3" />
    </a>
  </div>
);

/**
 * SettingsModal — "Your preferences" (redesign item 5).
 * Personal-only: every control here affects just the current user.
 * Global/scraper parameters live in Admin Console → Ranking.
 * Tabs: Topics · Keyword Filters · Notifications · Billing.
 */
const SettingsModal = ({ darkMode, authHeader, triggerTestId = "settings-modal-trigger" }) => {
  const [prefs, setPrefs] = useState(null); // per-user feed preferences
  const [newKeyword, setNewKeyword] = useState("");
  const [newExcludeKeyword, setNewExcludeKeyword] = useState("");
  const [saving, setSaving] = useState(false);
  const [hideReddit, setHideReddit] = useState(() => {
    try { return localStorage.getItem("ti_show_reddit") !== "true"; } catch { return true; }
  });
  const push = usePushNotifications(authHeader);
  const dm = darkMode;

  useEffect(() => {
    axios.get(`${API}/feed-prefs`, { headers: authHeader() })
      .then(r => setPrefs({ keywords: r.data.keywords || [], excludeKeywords: r.data.excludeKeywords || [], canEdit: r.data.canEdit !== false }))
      .catch(console.error);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/feed-prefs`, prefs, { headers: authHeader() });
      toast.success("Preferences saved");
      // refresh the feed so personal filters apply without a manual reload
      window.dispatchEvent(new CustomEvent("feed-prefs-saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const toggleHideReddit = (hidden) => {
    setHideReddit(hidden);
    try { localStorage.setItem("ti_show_reddit", String(!hidden)); } catch {}
    window.dispatchEvent(new CustomEvent("ti-reddit-pref-changed", { detail: { show: !hidden } }));
  };

  if (!prefs) return null;
  const inputClass = `border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`;
  const helperCls = `text-[11px] ${dm ? "text-slate-500" : "text-[#96a4a8]"}`;
  const addKeyword = (list, value, setter) => {
    const v = value.trim();
    if (v && !prefs[list].some(k => k.toLowerCase() === v.toLowerCase())) {
      setPrefs({ ...prefs, [list]: [...prefs[list], v] });
    }
    setter("");
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid={triggerTestId} className={`category-tag flex items-center gap-1.5 px-1 py-0.5 transition-colors ${dm ? "text-slate-400 hover:text-slate-100" : "text-[#6b7a7d] hover:text-[#191c1d]"}`}>
          <Settings className="w-3 h-3" />Settings
        </button>
      </DialogTrigger>
      <DialogContent className={`max-w-2xl max-h-[85vh] overflow-y-auto border-0 ${dm ? "bg-[#0c1011] text-slate-100" : "bg-white text-[#191c1d]"}`} style={{ boxShadow: "0 0 60px rgba(0,0,0,0.15)" }}>
        <DialogHeader className="pb-4">
          <DialogTitle className={`editorial-heading text-3xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Your preferences</DialogTitle>
          <DialogDescription className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Personal only — everything here affects just your feed</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="topics" className="w-full">
          <TabsList className={`grid w-full grid-cols-4 rounded-xl p-1 ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`}>
            <TabsTrigger value="topics" className="category-tag rounded-lg" data-testid="settings-topics-tab">Topics</TabsTrigger>
            <TabsTrigger value="keyword-filters" className="category-tag rounded-lg" data-testid="settings-keyword-filters-tab">Keyword Filters</TabsTrigger>
            <TabsTrigger value="notifications" className="category-tag rounded-lg" data-testid="settings-notifications-tab">Notifications</TabsTrigger>
            <TabsTrigger value="billing" className="category-tag rounded-lg" data-testid="settings-billing-tab">Billing</TabsTrigger>
          </TabsList>

          {/* ── Topics: followed topics (include keywords) + personal source toggles ── */}
          <TabsContent value="topics" className="space-y-5 mt-6">
            {!prefs.canEdit ? <ProTeaser dm={dm} /> : (
              <div>
                <p className={`text-sm font-semibold mb-1 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Topics you follow</p>
                <p className={`${helperCls} mb-3`} data-testid="topics-helper-note">
                  Show only stories matching at least one topic. Only affects your feed.
                </p>
                <div className="flex gap-2">
                  <Input data-testid="include-keyword-input" value={newKeyword} onChange={e => setNewKeyword(e.target.value)} placeholder="Add a topic, e.g. AI in hotels…" className={inputClass}
                    onKeyPress={e => { if (e.key === "Enter") addKeyword("keywords", newKeyword, setNewKeyword); }} />
                  <button data-testid="include-keyword-add-btn" onClick={() => addKeyword("keywords", newKeyword, setNewKeyword)}
                    className={`px-4 rounded-full category-tag ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3" data-testid="include-keywords-list">
                  {prefs.keywords.map((kw) => (
                    <span key={kw} onClick={() => setPrefs({ ...prefs, keywords: prefs.keywords.filter(k => k !== kw) })}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full category-tag cursor-pointer border ${dm ? "bg-[#0f2529] text-cyan-300 border-cyan-400/50" : "bg-white text-[#006874] border-[#006874]"}`}>
                      ✓ {kw}<X className="w-3 h-3" />
                    </span>
                  ))}
                  {prefs.keywords.length === 0 && (
                    <span className={helperCls}>No topics yet — your feed shows everything.</span>
                  )}
                </div>
              </div>
            )}

            <div className={`rounded-xl p-4 flex items-center justify-between gap-3 ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`}>
              <div>
                <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Hide Reddit sources</p>
                <p className={helperCls}>Only affects your feed.</p>
              </div>
              <Switch data-testid="hide-reddit-toggle" checked={hideReddit} onCheckedChange={toggleHideReddit} />
            </div>
          </TabsContent>

          {/* ── Keyword Filters: exclusions ── */}
          <TabsContent value="keyword-filters" className="space-y-5 mt-6">
            {!prefs.canEdit ? <ProTeaser dm={dm} /> : (
              <div>
                <p className={`text-sm font-semibold mb-1 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Exclude keywords</p>
                <p className={`${helperCls} mb-3`} data-testid="keyword-filters-helper-note">
                  Hide stories matching any of these keywords. Only affects your feed.
                </p>
                <div className="flex gap-2">
                  <Input data-testid="exclude-keyword-input" value={newExcludeKeyword} onChange={e => setNewExcludeKeyword(e.target.value)} placeholder="Add exclusion…" className={inputClass}
                    onKeyPress={e => { if (e.key === "Enter") addKeyword("excludeKeywords", newExcludeKeyword, setNewExcludeKeyword); }} />
                  <button data-testid="exclude-keyword-add-btn" onClick={() => addKeyword("excludeKeywords", newExcludeKeyword, setNewExcludeKeyword)}
                    className={`px-4 rounded-full category-tag ${dm ? "bg-red-400/10 text-red-400" : "bg-red-50 text-red-600"}`}><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3" data-testid="exclude-keywords-list">
                  {prefs.excludeKeywords.map((kw) => (
                    <span key={kw} onClick={() => setPrefs({ ...prefs, excludeKeywords: prefs.excludeKeywords.filter(k => k !== kw) })}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full category-tag cursor-pointer ${dm ? "bg-[#1a2122] text-red-400" : "bg-red-50 text-red-600"}`}>
                      {kw}<X className="w-3 h-3" />
                    </span>
                  ))}
                  {prefs.excludeKeywords.length === 0 && (
                    <span className={helperCls}>No exclusions yet.</span>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ── Notifications ── */}
          <TabsContent value="notifications" className="mt-6">
            <div className={`rounded-xl p-4 ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`} data-testid="notifications-section">
              {push.supported && push.permission !== "denied" ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Bell className={`w-4 h-4 mt-0.5 ${push.subscribed ? (dm ? "text-cyan-400" : "text-[#006874]") : "opacity-50"}`} />
                    <div>
                      <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Weekly brief notifications</p>
                      <p className={helperCls}>Get a push notification when a new brief is published. Only affects this browser.</p>
                    </div>
                  </div>
                  <Switch data-testid="notif-push-toggle" checked={!!push.subscribed} disabled={push.loading}
                    onCheckedChange={(on) => (on ? push.subscribe() : push.unsubscribe())} />
                </div>
              ) : (
                <p className={helperCls} data-testid="notif-unsupported-note">
                  {push.permission === "denied"
                    ? "Notifications are blocked for this site in your browser settings."
                    : "Push notifications aren't supported in this browser."}
                </p>
              )}
            </div>
          </TabsContent>

          {/* ── Billing ── */}
          <TabsContent value="billing" className="mt-6">
            <BillingSection dm={dm} />
          </TabsContent>
        </Tabs>
        {prefs.canEdit && (
          <div className="flex justify-end pt-4">
            <button data-testid="settings-save-btn" onClick={save} disabled={saving} className={`px-8 py-2.5 rounded-full category-tag disabled:opacity-50 ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
              {saving ? <span className="flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" />Saving…</span> : "Save preferences"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
